// Concurrency helpers
// --------------------

// Runs `fn` over every item in `items` with at most `limit` invocations
// in flight at any one time. Uses a rolling-window pool — as soon as
// one worker finishes its task it picks up the next item, so the pool
// stays saturated even if individual tasks have very different
// durations. Resolves once every item has been processed.
//
// Per-task failures are not caught here; if a task rejects, all
// in-flight workers eventually settle but the returned promise
// rejects with the first failure. Wrap fn in a try/catch if you want
// to swallow per-item errors.
export async function mapLimit<T>(
  items: readonly T[],
  limit: number,
  fn: (item: T) => Promise<void>
): Promise<void> {
  if (items.length === 0) return;
  const cap = Math.max(1, Math.min(limit, items.length));
  let cursor = 0;
  const workers: Promise<void>[] = [];
  for (let workerIndex = 0; workerIndex < cap; workerIndex++) {
    workers.push(
      (async () => {
        while (true) {
          const idx = cursor++;
          if (idx >= items.length) return;
          await fn(items[idx]);
        }
      })()
    );
  }
  await Promise.all(workers);
}

// Schedules a callback to run when the browser is idle, falling back
// to setTimeout(0) on environments that don't support
// requestIdleCallback (Safari < 16, older test environments, etc).
// Use for background work that should yield to UI / paint / first
// user interactions — e.g. eager-loading data that the user might
// drill into later.
export function scheduleIdle(callback: () => void): void {
  // requestIdleCallback isn't in lib.dom.d.ts on every TS target, so
  // we feature-test on the global at runtime.
  const globalRequestIdleCallback = (
    window as unknown as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
    }
  ).requestIdleCallback;
  if (typeof globalRequestIdleCallback === "function") {
    globalRequestIdleCallback(callback, { timeout: 2000 });
  } else {
    setTimeout(callback, 0);
  }
}
